import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { API_CONFIG, getApiUrl, getAuthHeaders } from '../config/api';
import { useThemedStyles } from '../theme/useThemedStyles';
import { ModalBackdrop } from '../components/common/ModalBackdrop';
import { formatDate } from '../utils/dateFormat';
import { BolivianDateInput } from '../components/common/BolivianDateInput';
import { clearDoctorsCache, setCachedDoctors } from '../utils/referenceDataCache';

interface Doctor {
  id: number;
  firstName: string;
  secondName?: string;
  lastName: string;
  secondLastName?: string;
  dateOfBirth: string;
  color?: string;
  defaultRoom?: number | null;
  commissionPercentage?: number | null;
  createdAt: string;
  firstDayInClinic: string;
  patients?: any[];
}

interface Props { 
  token: string; 
  onSelectDoctor?: (id: number) => void;
}

const container: React.CSSProperties = { padding: 24 };
const card: React.CSSProperties = { background: '#1e293b', border: '1px solid #334155', borderRadius: 8, padding: 16, display:'flex', flexDirection:'column', gap:8, color: '#f1f5f9' };
const grid: React.CSSProperties = { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px,1fr))', gap: 16 };
const modalOverlay: React.CSSProperties = { position:'fixed', inset:0, background:'rgba(0,0,0,0.7)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 };
const modalBox: React.CSSProperties = { background:'#1e293b', padding:32, borderRadius:12, width:'min(500px,90%)', maxHeight:'85vh', overflowY:'auto', display:'flex', flexDirection:'column', gap:16, color: '#f1f5f9', border: '1px solid #334155' };
const input: React.CSSProperties = { padding:'10px 12px', border:'2px solid #334155', borderRadius:6, fontSize:14, background: '#0f172a', color: '#f1f5f9' };
const actions: React.CSSProperties = { display:'flex', gap:12, flexWrap:'wrap' };
const primaryBtn: React.CSSProperties = { padding:'10px 20px', background:'#667eea', color:'#fff', border:'none', borderRadius:6, cursor:'pointer', fontWeight:500 };
const dangerBtn: React.CSSProperties = { ...primaryBtn, background:'#e74c3c' };
const secondaryBtn: React.CSSProperties = { ...primaryBtn, background:'#6c757d' };

export default function DoctorsList({ token, onSelectDoctor }: Props) {
  const { t } = useTranslation();
  const theme = useThemedStyles();
  const [doctors, setDoctors] = useState<Doctor[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<Doctor | null>(null);
  const [hiddenDoctorIds, setHiddenDoctorIds] = useState<Set<number>>(() => {
    try {
      const stored = localStorage.getItem('dentec-hidden-doctors');
      return stored ? new Set<number>(JSON.parse(stored)) : new Set<number>();
    } catch { return new Set<number>(); }
  });
  const [showHidden, setShowHidden] = useState(false);

  const toggleHidden = (id: number) => {
    setHiddenDoctorIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      localStorage.setItem('dentec-hidden-doctors', JSON.stringify(Array.from(next)));
      return next;
    });
  };
  const [form, setForm] = useState({
    firstName:'', secondName:'', lastName:'', secondLastName:'', dateOfBirth:'', firstDayInClinic:'', color:'#667eea', defaultRoom:'', commissionPercentage:''
  });

  const fetchDoctors = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await fetch(getApiUrl(API_CONFIG.ENDPOINTS.DOCTORS), { headers: getAuthHeaders(token) });
      if (!res.ok) {
        let body: string = '';
        try { body = await res.text(); } catch {}
        throw new Error(`Failed to load doctors (HTTP ${res.status}) ${body}`);
      }
      const data = await res.json();
      setCachedDoctors(data);
      setDoctors(data);
    } catch (e: any) { setError(e.message); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchDoctors(); }, [fetchDoctors]);

  const openCreate = () => { setEditing(null); setForm({ firstName:'', secondName:'', lastName:'', secondLastName:'', dateOfBirth:'', firstDayInClinic:'', color:'#667eea', defaultRoom:'', commissionPercentage:'' }); setShowModal(true); };
  const openEdit = (d: Doctor) => { setEditing(d); setForm({ firstName:d.firstName, secondName:d.secondName||'', lastName:d.lastName, secondLastName:d.secondLastName||'', dateOfBirth:d.dateOfBirth?.slice(0,10)||'', firstDayInClinic:d.firstDayInClinic?.slice(0,10)||'', color:d.color||'#667eea', defaultRoom:d.defaultRoom ? String(d.defaultRoom) : '', commissionPercentage:d.commissionPercentage == null ? '' : String(d.commissionPercentage) }); setShowModal(true); };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const payload = {
      ...form,
      defaultRoom: form.defaultRoom ? parseInt(form.defaultRoom, 10) : null,
      commissionPercentage: form.commissionPercentage === '' ? null : Number(form.commissionPercentage),
      dateOfBirth: form.dateOfBirth ? new Date(form.dateOfBirth).toISOString() : new Date().toISOString(),
      firstDayInClinic: form.firstDayInClinic ? new Date(form.firstDayInClinic).toISOString() : new Date().toISOString(),
      createdAt: editing?.createdAt || new Date().toISOString()
    };
    const method = editing ? 'PUT' : 'POST';
    const url = editing ? getApiUrl(`${API_CONFIG.ENDPOINTS.DOCTORS}/${editing.id}`) : getApiUrl(API_CONFIG.ENDPOINTS.DOCTORS);
    const res = await fetch(url, { method, headers: getAuthHeaders(token), body: JSON.stringify(payload) });
    if (!res.ok) { alert(t('doctors.saveFailed')); return; }
    clearDoctorsCache();
    setShowModal(false); fetchDoctors();
  };

  const del = async () => {
    if (!editing) return; if (!window.confirm(t('doctors.deleteConfirm'))) return;
    const res = await fetch(getApiUrl(`${API_CONFIG.ENDPOINTS.DOCTORS}/${editing.id}`), { method:'DELETE', headers: getAuthHeaders(token) });
    if (!res.ok) { alert(t('doctors.deleteFailed')); return; }
    clearDoctorsCache();
    setShowModal(false); fetchDoctors();
  };

  const themedContainer: React.CSSProperties = { ...container, color: theme.text };
  const themedCard: React.CSSProperties = { ...card, ...theme.card, padding: 16, gap: 8 };
  const themedModalBox: React.CSSProperties = { ...modalBox, background: theme.surface, color: theme.text, border: `1px solid ${theme.border}` };
  const themedInput: React.CSSProperties = { ...input, ...theme.inputStrong };
  const themedPrimaryBtn: React.CSSProperties = { ...primaryBtn, ...theme.button };
  const themedSecondaryBtn: React.CSSProperties = { ...secondaryBtn, ...theme.secondaryButton };

  if (loading) return <div style={themedContainer}>{t('doctors.loading')}</div>;
  if (error) return <div style={themedContainer}>{t('doctors.error', { message: error })}</div>;

  return (
    <div style={themedContainer}>
      <h2 style={{marginTop:0}}>{t('doctors.title')}</h2>
      <div className="responsive-row" style={{ display:'flex', gap:12, marginBottom:16, flexWrap:'wrap', alignItems:'center' }}>
        <button onClick={fetchDoctors} style={{ ...themedPrimaryBtn, padding:'8px 16px' }}>{t('doctors.refresh')}</button>
        <button onClick={openCreate} style={{ padding:'8px 16px', border:'1px solid #28a745', background:'#28a745', color:'#fff', borderRadius:6, cursor:'pointer' }}>{t('doctors.newDoctor')}</button>
        {hiddenDoctorIds.size > 0 && (
          <button
            onClick={() => setShowHidden(v => !v)}
            style={{ padding:'8px 16px', border:`1px solid ${theme.border}`, background: showHidden ? theme.surfaceAlt : 'transparent', color:theme.muted, borderRadius:6, cursor:'pointer' }}
          >
            {showHidden ? `👁 ${t('doctors.hideHidden')} (${hiddenDoctorIds.size})` : `👁 ${t('doctors.showHidden')} (${hiddenDoctorIds.size})`}
          </button>
        )}
      </div>
      <div className="responsive-grid" style={grid}>
        {doctors.filter(d => showHidden || !hiddenDoctorIds.has(d.id)).map(d => {
          const isHidden = hiddenDoctorIds.has(d.id);
          const fullName = [d.firstName, d.secondName, d.lastName, d.secondLastName].filter(Boolean).join(' ');
          return (
            <div key={d.id} style={{...themedCard, cursor: onSelectDoctor ? 'pointer' : 'default', opacity: isHidden ? 0.45 : 1}} onClick={() => onSelectDoctor && onSelectDoctor(d.id)}>
              <div className="responsive-row" style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                <div className="responsive-row" style={{ display:'flex', alignItems:'center', gap:8 }}>
                  <div style={{ width:14, height:14, borderRadius:'50%', background: d.color || '#667eea' }} />
                  <strong>{fullName}</strong>
                  {isHidden && <span style={{ fontSize:10, color:theme.muted, background:theme.surface, border:`1px solid ${theme.border}`, borderRadius:3, padding:'1px 4px' }}>{t('doctors.hidden')}</span>}
                </div>
                <div className="responsive-row" style={{ display:'flex', gap:6 }}>
                  <button
                    onClick={(e) => { e.stopPropagation(); toggleHidden(d.id); }}
                    style={{ padding:'4px 8px', fontSize:12, background:'transparent', border:`1px solid ${theme.border}`, color: theme.muted, borderRadius:4, cursor:'pointer' }}
                    title={isHidden ? t('doctors.showDoctor') : t('doctors.hideDoctor')}
                  >
                    {isHidden ? '🚫' : '👁'}
                  </button>
                  <button 
                    onClick={(e) => { e.stopPropagation(); openEdit(d); }}
                    style={{ padding:'4px 8px', fontSize:12, background:'transparent', border:`1px solid ${theme.border}`, color:theme.muted, borderRadius:4, cursor:'pointer' }}
                    title={t('doctors.editDoctor')}
                  >
                    ✎
                  </button>
                </div>
              </div>
              <div style={{ fontSize:12, color:theme.muted }}>{t('doctors.firstDayLabel')} {formatDate(d.firstDayInClinic)}</div>
              <div style={{ fontSize:12, color:theme.muted }}>{t('doctors.defaultRoomLabel')} {d.defaultRoom ? t('doctors.room', { room: d.defaultRoom }) : t('doctors.noDefaultRoom')}</div>
              <div style={{ fontSize:12, color:theme.muted }}>{t('doctors.commissionPercentage', 'Default doctor share')}: {d.commissionPercentage == null ? '—' : `${d.commissionPercentage}%`}</div>
              <div style={{ fontSize:12, color:theme.muted }}>{t('doctors.createdLabel')} {formatDate(d.createdAt)}</div>
              <div style={{ fontSize:12, color:theme.muted }}>{t('doctors.patientsLabel')} {d.patients ? d.patients.length : '—'}</div>
            </div>
          );
        })}
      </div>

      {showModal && (
        <ModalBackdrop className="responsive-row" style={modalOverlay} onDismiss={() => setShowModal(false)}>
          <div style={themedModalBox} onClick={e => e.stopPropagation()}>
            <h3 style={{margin:'0 0 8px'}}>{editing ? t('doctors.editDoctor') : t('doctors.newDoctor')}</h3>
            <form onSubmit={submit} style={{ display:'flex', flexDirection:'column', gap:14 }}>
              <div className="responsive-row" style={{ display:'flex', gap:12 }}>
                <input style={{ ...themedInput, flex:1 }} placeholder={t('doctors.firstName')} value={form.firstName} onChange={e=>setForm({...form, firstName:e.target.value})} required />
                <input style={{ ...themedInput, flex:1 }} placeholder={t('doctors.secondName')} value={form.secondName} onChange={e=>setForm({...form, secondName:e.target.value})} />
              </div>
              <div className="responsive-row" style={{ display:'flex', gap:12 }}>
                <input style={{ ...themedInput, flex:1 }} placeholder={t('doctors.lastName')} value={form.lastName} onChange={e=>setForm({...form, lastName:e.target.value})} required />
                <input style={{ ...themedInput, flex:1 }} placeholder={t('doctors.secondLastName')} value={form.secondLastName} onChange={e=>setForm({...form, secondLastName:e.target.value})} />
              </div>
              <div className="responsive-row" style={{ display:'flex', gap:12 }}>
                <label style={{ display:'flex', flexDirection:'column', fontSize:12, flex:1 }}>{t('doctors.dob')}
                  <BolivianDateInput style={themedInput} value={form.dateOfBirth} onChange={dateOfBirth => setForm({ ...form, dateOfBirth })} required />
                </label>
                <label style={{ display:'flex', flexDirection:'column', fontSize:12, flex:1 }}>{t('doctors.firstDay')}
                  <BolivianDateInput style={themedInput} value={form.firstDayInClinic} onChange={firstDayInClinic => setForm({ ...form, firstDayInClinic })} required />
                </label>
              </div>
              <div className="responsive-row" style={{ display:'flex', gap:12, alignItems:'center' }}>
                <label style={{ fontSize:12 }}>{t('doctors.color')}</label>
                <input type='color' value={form.color} onChange={e=>setForm({...form, color:e.target.value})} style={{ width:48, height:40, border:'none', background:'transparent', cursor:'pointer' }} />
                <input style={{ ...themedInput, flex:1 }} value={form.color} onChange={e=>setForm({...form, color:e.target.value})} />
              </div>
              <label style={{ display:'flex', flexDirection:'column', fontSize:12, gap:6 }}>{t('doctors.defaultRoom')}
                <select style={themedInput} value={form.defaultRoom} onChange={e=>setForm({...form, defaultRoom:e.target.value})}>
                  <option value="">{t('doctors.noDefaultRoom')}</option>
                  {[1, 2, 3, 4, 5].map(room => <option key={room} value={room}>{t('doctors.room', { room })}</option>)}
                </select>
              </label>
              <label style={{ display:'flex', flexDirection:'column', fontSize:12, gap:6 }}>{t('doctors.commissionPercentage', 'Default doctor share (%)')}
                <input style={themedInput} type="number" min="0" max="100" step="0.01" value={form.commissionPercentage} onChange={e=>setForm({...form, commissionPercentage:e.target.value})} placeholder={t('doctors.commissionOptional', 'Optional')} />
              </label>
              <div className="responsive-row" style={actions}>
                <button type='submit' style={themedPrimaryBtn}>{editing ? t('doctors.saveChanges') : t('doctors.createDoctor')}</button>
                {editing && <button type='button' style={dangerBtn} onClick={del}>{t('doctors.delete')}</button>}
                <button type='button' style={themedSecondaryBtn} onClick={()=>setShowModal(false)}>{t('doctors.cancel')}</button>
              </div>
            </form>
          </div>
        </ModalBackdrop>
      )}
    </div>
  );
}


